import React, { useEffect, useState } from 'react';
import flatMap from 'lodash/flatMap';
import { useTranslation } from 'react-i18next';
import groupBy from 'lodash/groupBy';
import chunk from 'lodash/chunk';
import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';

import {
  updateCart,
  updateCartWithItems,
  getCart,
  getBaBSingleProducts,
} from './api';
import { INotifOptions } from '../utils/useNotif';

import {
  extractVariantAndImage,
  getOnlySelectedVariants,
  formatLiquidProductToGlobalProduct,
} from '../utils/BundleUtils';
import {
  getDiscountTypeFromURL,
  isGlobalProductList,
  PERCENTAGE_PARAM_TYPE,
  notEmpty,
  getIdFromGid,
} from '../utils';
import isEmpty from 'lodash/isEmpty';
import {
  BuildABoxFilterCondition,
  BuildABoxFilterType,
  FilterConditional,
  VariantStyle,
} from '../../components/BundleSettings/types';

const MAX_CHUNK_SIZE = 19;

export interface ErrorApiObject {
  message: string;
  cause: { product: string };
}

export const getProductsLength = (
  selectedProducts: Record<string, number>,
): number => {
  return Object.keys(selectedProducts).reduce((acc, key) => {
    return acc + selectedProducts[key];
  }, 0);
};

export const getBundleUrlParams = () => {
  const urlParams = new URLSearchParams(window?.location.search);
  const variant = urlParams.get('variant') || '';
  const selling_plan = urlParams.get('selling_plan') || '';

  return { VariantId: variant, SellingPlan: selling_plan };
};

export const goBack = (): void => {
  window.history.back();
};

export const getTransformedBodyItems = (
  bundleId: string,
  sellingPlan: string,
  itemProperties: BundleCartProperties,
): BundleCartBodyItem => {
  const bodyParams: BundleCartBodyItem = {
    quantity: 1,
    id: bundleId,
    properties: itemProperties,
  };

  if (sellingPlan) {
    bodyParams.selling_plan = sellingPlan;
  }

  return bodyParams;
};

export const getTransformedBodyAddons = (
  itemProperties: BundleCartProperties,
  sellingPlan: string,
  addons: Record<string, number>,
  productsInAddonsCollection: GlobalVariant[] | GlobalProduct[],
): BundleCartBodyAddon[] => {
  const discountType = getDiscountTypeFromURL();

  const addonsVariantsSelected: GlobalVariant[] = isGlobalProductList(
    productsInAddonsCollection,
  )
    ? (productsInAddonsCollection.flatMap(
        ({ variants }) => variants,
      ) as GlobalVariant[])
    : productsInAddonsCollection;

  return Object.keys(addons)
    .filter((key) =>
      addonsVariantsSelected.find((p) => p.id.toString() === key),
    )
    .map((key) => {
      const addOnProduct = addonsVariantsSelected.find(
        (p) => p.id.toString() === key,
      );

      const addOn: BundleCartBodyAddon = {
        id: key,
        quantity: addons[key],
        properties: {
          _bundleProductId: itemProperties._bundleProductId,
          _addon: 1,
        },
      };

      if (
        sellingPlan &&
        addOnProduct?.sellingPlanAllocations?.find(
          (spa) => spa.selling_plan_id.toString() === sellingPlan.toString(),
        ) &&
        (!discountType || discountType === PERCENTAGE_PARAM_TYPE)
      ) {
        addOn.selling_plan = sellingPlan;
      }

      return addOn;
    });
};

export const useAddBundleToCartAndRedirect = (
  bundle: Bundle = {} as Bundle,
  addNotif: (notif: string, opts: INotifOptions | undefined) => void,
): useAddBundleToCartAndRedirect => {
  const BaBProductsToCartFlag = bundle?.Type === 'dynamicBaB';
  const { t } = useTranslation();
  const [isAddingToCart, setIsAddingToCart] = useState<boolean>(false);
  const [giftItemProperties, setGiftItemProperties] = useState({});

  useEffect(() => {
    const giftInformation = JSON.parse(
      sessionStorage.getItem('awt-gift-information') || '{}',
    );
    if (!isEmpty(giftInformation)) {
      setGiftItemProperties({
        _giftFirstName: giftInformation.firstName,
        _giftLastName: giftInformation.lastName,
        _giftEmail: giftInformation.email,
        _giftNote: giftInformation.note,
      });
      sessionStorage.setItem('awt-gift-information', '{}');
    }
  }, []);

  const addBundleToCartAndRedirect = async ({
    bundleId,
    sellingPlan,
    itemProperties,
    addons,
    productsInAddonsCollection,
    productsWithProps,
    discountCode,
  }: AddBundleToCartAndRedirectProps) => {
    try {
      setIsAddingToCart(true);
      const productsWithPropsAndGifts = productsWithProps.map(
        (productWithProps) => ({
          ...productWithProps,
          properties: {
            ...productWithProps.properties,
            ...giftItemProperties,
          },
        }),
      );

      const individualProductsCollection: LiquidProductInCollection[] =
        window?.reactInit?.individualProductsCollection || [];

      const productsInAddonsCollectionRaw: LiquidProductInCollection[] =
        window?.reactInit?.productsInAddonsCollection || [];

      let cartItems: (BundleCartBodyItem | BundleCartBodyAddon)[] = [];
      if (!BaBProductsToCartFlag) {
        const bodyItem = getTransformedBodyItems(bundleId, sellingPlan, {
          ...itemProperties,
          ...giftItemProperties,
        });
        const bodyAddons = getTransformedBodyAddons(
          itemProperties,
          sellingPlan,
          addons,
          productsInAddonsCollection,
        );

        cartItems = [bodyItem, ...bodyAddons];
        if (window.bundleapp?.settings?.bab?.addToCart) {
          await window.bundleapp.settings.bab.addToCart({
            dynamic: false,
            items: [bodyItem],
            bodyAddons,
          });
        } else {
          await updateCart(
            bodyItem,
            bodyAddons,
            individualProductsCollection,
            productsInAddonsCollectionRaw,
          );
        }
      } else {
        const bodyAddons = getTransformedBodyAddons(
          { ...itemProperties, ...giftItemProperties },
          sellingPlan,
          addons,
          productsInAddonsCollection,
        );

        cartItems = [...productsWithPropsAndGifts, ...bodyAddons];
        if (window.bundleapp?.settings?.bab?.addToCart) {
          await window.bundleapp.settings.bab.addToCart({
            dynamic: true,
            items: productsWithPropsAndGifts,
            bodyAddons,
          });
        } else {
          await updateCartWithItems(
            productsWithPropsAndGifts,
            bodyAddons,
            individualProductsCollection,
            productsInAddonsCollectionRaw,
          );
        }
      }

      const shopifyRoot = window.Shopify?.routes?.root || '/';

      if (window.bundleapp?.settings?.bab?.addToCartRedirect) {
        await window.bundleapp.settings.bab.addToCartRedirect({
          cartItems,
          discountCode,
        });
      } else if (discountCode) {
        window.location.assign(
          `${shopifyRoot}discount/${discountCode}?redirect=${shopifyRoot}cart`,
        );
      } else {
        window.location.assign(`${shopifyRoot}cart`);
      }
    } catch (e) {
      if (e instanceof ExceptionErrorApiObject) {
        const cause = (e as any).cause as any;
        addNotif(
          t(e.message as string, {
            stock: cause.stock,
            product: cause?.product,
          }),
          { type: 'error' },
        );
      }

      if (e instanceof Error && !(e as any)?.cause) {
        if (typeof e.message === 'string') {
          addNotif(t(e.message as string), { type: 'error' });
        }
      }

      setIsAddingToCart(false);
    } finally {
      // We wait for 1 second to allow the page cart to redirect
      // before enabling the button again. This is to prevent adding the same item twice.
      setTimeout(() => {
        setIsAddingToCart(false);
      }, 1000);
    }
  };

  return { addBundleToCartAndRedirect, isAddingToCart };
};

const transformProductsInBundle = (
  customAttributes: Record<string, string> = {},
): Record<string, number> => {
  const productsInBundle = {};
  Object.entries(customAttributes).forEach(([key, value]) => {
    if (key.startsWith('_pv_')) {
      const productId = key.replace('_pv_', '');
      productsInBundle[productId] = +value;
    }
  });
  return productsInBundle;
};

export const useGetInitialProducts = (
  bundleUrlParams: {
    VariantId: string;
  },
  isPreview: boolean,
  selectedProducts?: Pick<Bundle, 'SelectedProducts'>,
): Record<string, number> => {
  const [initialSelectedProducts, setInitialSelectedProducts] = React.useState<
    Record<string, number>
  >({});
  React.useEffect(() => {
    const fillFromCart = async (): Promise<void> => {
      const cart = await getCart();
      const cartItems = cart.items;
      const bundle = cartItems.find(
        (item: CartItem) =>
          item.variant_id.toString() === bundleUrlParams.VariantId,
      );

      if (bundle) {
        const products = transformProductsInBundle(bundle.properties);
        setInitialSelectedProducts(products);
      } else if (selectedProducts) {
        setInitialSelectedProducts(
          selectedProducts[bundleUrlParams.VariantId] ||
            selectedProducts[Object.keys(selectedProducts)?.[0]] ||
            {},
        );
      }
    };
    if (!isPreview) {
      fillFromCart();
    }
  }, [bundleUrlParams.VariantId, selectedProducts]);

  return initialSelectedProducts;
};

export const removeUnexistingProducts = (
  initialSelectedProducts: Record<string, number> = {},
  productsInCollection: GlobalVariant[] | GlobalProduct[] = [],
): Record<string, number> => {
  let productsInBundle = {};

  if (isGlobalProductList(productsInCollection)) {
    productsInBundle = flatMap(productsInCollection, ({ variants }) => variants)
      .filter(({ id }) =>
        Object.keys(initialSelectedProducts).includes(`${id}`),
      )
      .filter((variant, i, variants) => {
        // removed variants from the same card in collapsed variants.
        const isMultipleVariant =
          (productsInCollection.find(
            (p) => p.id === variant.productId || p.id === variant.id,
          )?.variants?.length || 2) > 1;

        return (
          !isMultipleVariant ||
          variants.findIndex((v) => v.productId === variant.productId) === i
        );
      })
      .reduce(
        (acc, { id }) => ({ ...acc, [id]: initialSelectedProducts[id] }),
        {},
      );
  } else {
    Object.keys(initialSelectedProducts).forEach((key) => {
      const existProduct = productsInCollection.some(
        ({ id }) => id.toString() === key,
      );

      if (existProduct) {
        productsInBundle[key] = initialSelectedProducts[key];
      }
    });
  }

  return productsInBundle;
};

export const fetchIndividualProducts = async (
  products: string[],
): Promise<LiquidProductInCollection[]> => {
  const chunkOfProducts = chunk(products, MAX_CHUNK_SIZE);

  const result = await Promise.all(
    chunkOfProducts.map((chunk) => getBaBSingleProducts(chunk.join(','))),
  );

  return flatten(result);
};

export const useGetIndividualProducts = ({
  isVariantCollapsed,
  productsInfo = [],
  defaultIndividualProducts = [],
  productsInCollection = [],
}: {
  isVariantCollapsed: boolean;
  productsInfo?: IndividualProductData[];
  defaultIndividualProducts?: LiquidProductInCollection[];
  productsInCollection: LiquidProductInCollection[];
}): GlobalVariant[] | GlobalProduct[] => {
  const [individualProducts, setIndividualProducts] = React.useState<
    GlobalVariant[] | GlobalProduct[]
  >([]);

  React.useEffect(() => {
    const getIndividualProducts = async (): Promise<void> => {
      let singleVariantsCollection: GlobalVariant[] | GlobalProduct[] = [];

      const individualProductsCollection =
        productsInfo?.length && !defaultIndividualProducts.length
          ? await fetchIndividualProducts(
              productsInfo.map(({ Handle }) => Handle),
            )
          : defaultIndividualProducts;

      if (individualProductsCollection && productsInfo?.length) {
        if (!isVariantCollapsed) {
          const variants = flatMap(productsInfo, ({ Variants }) => {
            return Variants;
          });

          singleVariantsCollection = flatMap(
            individualProductsCollection,
            (variantData) => extractVariantAndImage(variantData),
          ).filter((variant) => getOnlySelectedVariants(variant, variants));
        } else {
          singleVariantsCollection = individualProductsCollection
            .map(({ productData, productMetafields, variantsInventory }) =>
              formatLiquidProductToGlobalProduct(
                {
                  ...productData,
                  metafields: productMetafields,
                },
                productsInfo,
                variantsInventory,
              ),
            )
            .filter(notEmpty);
        }
      } else {
        if (!isVariantCollapsed) {
          singleVariantsCollection = flatMap(
            productsInCollection,
            (productData) => extractVariantAndImage(productData),
          );
        } else {
          singleVariantsCollection = productsInCollection
            .map(({ productData, productMetafields, variantsInventory }) =>
              formatLiquidProductToGlobalProduct(
                {
                  ...productData,
                  metafields: productMetafields,
                },
                productsInfo,
                variantsInventory,
              ),
            )
            .filter(notEmpty);
        }
      }
      setIndividualProducts(singleVariantsCollection);
    };
    getIndividualProducts();
  }, [isVariantCollapsed]);

  return individualProducts;
};

/** Returns a list of selected products with the quantities
  - reduced to achieve the selected size of the bundle
  - @param products: object que the selected product as key and quantity as value { [productId]: quantity }
  - @param selectedBoxSize: Size to achieve in the bundle
  - @returns: object with the selected products and quantities
*/
export const removeProductsForCorrectSize = (
  products: Record<string, number>,
  selectedBoxSize: number,
): Record<string, number> => {
  let productsCounter = getProductsLength(products);
  const productsToRemove = { ...products };

  while (productsCounter > selectedBoxSize) {
    Object.keys(productsToRemove).forEach((key) => {
      if (productsCounter < selectedBoxSize || !productsToRemove[key]) {
        return;
      }
      productsToRemove[key]--;
      productsCounter = getProductsLength(productsToRemove);
    });
  }

  return productsToRemove;
};

enum API_ERROR {
  SELLING_PLAN_ERROR,
  OUT_OF_STOCK_ITEMS,
  SOLD_OUT_ITEM,
  ALREADY_IN_CART,
  UNKNOWN_ERROR,
}

interface ApiError {
  message: string;
  description: string;
  status: number;
}

export const handleCartApiError = (
  apiError: ApiError,
  individualProductsCollection: LiquidProductInCollection[],
  productsInAddonsCollection: LiquidProductInCollection[],
): string | ErrorApiObject => {
  switch (getKnownErrors(apiError.description)) {
    case API_ERROR.OUT_OF_STOCK_ITEMS:
      return calculateRemainingStockAndReturnMessage(
        apiError.description,
        individualProductsCollection,
        productsInAddonsCollection,
      );
    case API_ERROR.SELLING_PLAN_ERROR:
      return 'bundles.cartItemNoSellingPlan';
    case API_ERROR.SOLD_OUT_ITEM: {
      const sentence = apiError.description;
      const productName = sentence.match(/'([^']+)'/)?.[1] ?? '';

      return {
        message: 'bundles.cartOutOfStockWithDetails',
        cause: { product: productName },
      };
    }
    case API_ERROR.ALREADY_IN_CART: {
      const sentence = apiError.description;
      const productName =
        sentence.match(/All \d+ (.+?) are in your cart\./)?.[1] ?? '';

      return {
        message: 'bundles.cartAlreadyInCart',
        cause: { product: productName },
      };
    }
    case API_ERROR.UNKNOWN_ERROR:
    default:
      // If we cannot match the cart error, we will show the shopify description.
      // This affects all stores in other languages than english
      return apiError.description;
  }
};

const calculateRemainingStockAndReturnMessage = (
  description: string,
  individualProductsCollection: LiquidProductInCollection[],
  productsInAddonsCollection: LiquidProductInCollection[],
): ErrorApiObject | string => {
  const oosProductName = description
    .split('add more')[1]
    .split('to the cart')[0]
    ?.trim();

  if (
    individualProductsCollection.length === 0 &&
    productsInAddonsCollection.length === 0
  ) {
    return description;
  }
  const outOfStockProduct =
    individualProductsCollection.find(({ productData }) =>
      oosProductName.includes(productData.title),
    ) ||
    productsInAddonsCollection.find(({ productData }) =>
      oosProductName.includes(productData.title),
    ) ||
    ({} as LiquidProductInCollection);

  if (!outOfStockProduct) return 'bundles.cartOutOfStock';

  const outOfStockVariantId =
    outOfStockProduct?.productData?.variants.find(
      (v) => oosProductName.includes(v.title) || v.title === 'Default Title',
    )?.id || '';

  const variantsInStock = outOfStockProduct
    ? Object.keys(outOfStockProduct?.variantsInventory || {})?.find(
        (key) => key === `${outOfStockVariantId}`,
      )?.[1] || '0'
    : '0';

  if (!variantsInStock || variantsInStock === '0') {
    return 'bundles.cartOutOfStock';
  }

  if (!outOfStockProduct) {
    return 'bundles.cartOutOfStock';
  }

  return {
    message: 'bundles.cartOutOfStockWithDetails',
    cause: { product: oosProductName },
  };
};

const getKnownErrors = (description: string): API_ERROR => {
  if (
    description.includes('purchased with a selling plan') ||
    description.includes('selling_plan')
  ) {
    return API_ERROR.SELLING_PLAN_ERROR;
  }

  if (description.includes("You can't add more")) {
    return API_ERROR.OUT_OF_STOCK_ITEMS;
  }

  if (description.includes('is already sold out')) {
    return API_ERROR.SOLD_OUT_ITEM;
  }

  if (description.includes('are in your cart')) {
    return API_ERROR.ALREADY_IN_CART;
  }

  if (description.includes('is already in your cart')) {
    return API_ERROR.ALREADY_IN_CART;
  }

  if (description.includes('were added to your cart')) {
    return API_ERROR.OUT_OF_STOCK_ITEMS;
  }

  return API_ERROR.UNKNOWN_ERROR;
};

export const getUrlSelectedProducts = (
  query: URLSearchParams,
  products?: IndividualProductData[],
  productsInCollection?: LiquidProductInCollection[],
  productsInSections?: BabSection[],
): Record<string, number> | null => {
  if (
    (!products || !Array.isArray(products)) &&
    (!productsInSections || productsInSections?.length === 0)
  ) {
    return null;
  }

  let variantIds = [];
  let collectionVariantsId = [];
  if (productsInSections?.length > 0) {
    variantIds =
      productsInSections
        ?.flatMap(
          ({ products }) => products?.flatMap(({ Variants }) => Variants || []),
        )
        .map(({ VariantId }) => getIdFromGid(VariantId)) || [];

    collectionVariantsId = productsInSections
      ?.flatMap(({ productsInCollections }) => productsInCollections)
      .flatMap((product) =>
        product.map(
          ({ productData }) =>
            productData?.variants?.flatMap?.(({ id }) => id.toString()),
        ),
      )
      ?.flatMap((ids) => ids);
    3;
  } else {
    variantIds =
      products
        ?.flatMap(({ Variants }) => Variants)
        .map(({ VariantId }) => getIdFromGid(VariantId)) || [];

    collectionVariantsId =
      productsInCollection
        ?.map(({ productData }) => productData)
        .flatMap(({ variants }) => variants)
        .map(({ id }) => id.toString()) || [];
  }

  const selectedVariants = {};

  for (const [key, value] of query) {
    if (variantIds.includes(key) || collectionVariantsId.includes(key)) {
      selectedVariants[key] = parseInt(value);
    }
  }
  return !isEmpty(selectedVariants) ? selectedVariants : null;
};

export const overwriteSelectedProducts = (
  query: URLSearchParams,
  urlSelectedProducts: Record<string, number> | null,
  selectedProducts?: Record<string, Record<string, number>>,
): Record<string, Record<string, number>> => {
  const overwrittenItems = { ...(selectedProducts || {}) };
  const babSize = query.get('variant');

  if (!urlSelectedProducts) return overwrittenItems;

  if (!babSize) {
    const firstBabSize = Object.keys(overwrittenItems)?.[0];
    return {
      ...overwrittenItems,
      [firstBabSize]: urlSelectedProducts,
    };
  }

  return {
    ...overwrittenItems,
    [babSize]: urlSelectedProducts,
  };
};

const FILTER_TYPES_KEY_LOOKUP = {
  variantTitle: 'title',
  productTag: 'tags',
};

export const removeDuplicateProductsAcrossSections = ({
  productsInSections,
}: {
  productsInSections: BabSection[];
}) =>
  productsInSections?.reduce((acc, curr) => {
    const { productsInCollections } = curr;
    const allProductVariantsInSection = uniqBy(
      productsInCollections
        ?.flat()
        .map(({ productData }) => productData?.variants)
        .flat(),
      'id',
    );

    const existingProductVariantsInBaB = acc
      .map((section) => {
        return section.productsInCollections
          ?.flat()
          .map(({ productData }) => productData?.variants)
          .flat();
      })
      .flat()
      .filter(Boolean);

    const newProductVariantsInCollections = allProductVariantsInSection?.filter(
      ({ id }) =>
        !existingProductVariantsInBaB?.map(({ id }) => id).includes(id),
    );

    const newProductsInCollections = productsInCollections?.map((collection) =>
      collection
        .map(({ productData, ...rest }) => {
          const newVariants = productData?.variants.filter(
            ({ id }) =>
              newProductVariantsInCollections?.map(({ id }) => id).includes(id),
          );
          //if the product has no variants remove the product
          if (newVariants.length === 0) return;
          const newProductData = { ...productData, variants: newVariants };
          return { ...rest, productData: newProductData };
        })
        .filter(Boolean),
    );

    return [
      ...acc,
      { ...curr, productsInCollections: newProductsInCollections },
    ];
  }, []);

const filterByRules = ({
  acc,
  FilterCondition,
  FilterType,
  FilterValue,
  variant,
  product,
  isVariantStyleCollapse,
  filterConditional = FilterConditional.OR,
}: {
  acc: boolean | undefined;
  FilterCondition: BuildABoxFilterCondition;
  FilterType: BuildABoxFilterType;
  FilterValue: string;
  variant?: GlobalVariant;
  product: GlobalProduct;
  isVariantStyleCollapse: boolean;
  filterConditional: FilterConditional;
}) => {
  const key = FILTER_TYPES_KEY_LOOKUP[FilterType];
  const lowerCaseFilterValue = FilterValue.toLowerCase();
  const isVariantTitle = key === FILTER_TYPES_KEY_LOOKUP.variantTitle;

  if (filterConditional === FilterConditional.OR && acc === true) return true;
  if (filterConditional === FilterConditional.AND && acc === false)
    return false;

  switch (FilterCondition) {
    case BuildABoxFilterCondition.contains: {
      if (isVariantTitle) {
        return isVariantStyleCollapse
          ? variant && product.hasOnlyDefaultVariant
            ? variant.originalVariantTitle
                ?.toLowerCase()
                .includes(lowerCaseFilterValue)
            : variant[key].toLowerCase().includes(lowerCaseFilterValue)
          : product.hasOnlyDefaultVariant
            ? product.originalVariantTitle
                ?.toLowerCase()
                .includes(lowerCaseFilterValue)
            : product[key].toLowerCase().includes(lowerCaseFilterValue);
      } else {
        return product[key].some((tag) =>
          tag.toLowerCase().includes(lowerCaseFilterValue),
        );
      }
    }
    case BuildABoxFilterCondition.notContains: {
      if (isVariantTitle) {
        return isVariantStyleCollapse
          ? variant && product.hasOnlyDefaultVariant
            ? !variant.originalVariantTitle
                ?.toLowerCase()
                .includes(lowerCaseFilterValue)
            : !variant[key].toLowerCase().includes(lowerCaseFilterValue)
          : product.hasOnlyDefaultVariant
            ? !product.originalVariantTitle
                ?.toLowerCase()
                .includes(lowerCaseFilterValue)
            : !product[key].toLowerCase().includes(lowerCaseFilterValue);
      } else {
        return (
          product[key].every(
            (tag) => !tag.toLowerCase().includes(lowerCaseFilterValue),
          ) || product[key].length === 0
        );
      }
    }
    case BuildABoxFilterCondition.isEqual: {
      if (isVariantTitle) {
        return isVariantStyleCollapse
          ? variant && product.hasOnlyDefaultVariant
            ? variant.originalVariantTitle?.toLowerCase() ===
              lowerCaseFilterValue
            : variant[key].toLowerCase() === lowerCaseFilterValue
          : product.hasOnlyDefaultVariant
            ? product.originalVariantTitle?.toLowerCase() ===
              lowerCaseFilterValue
            : product[key].toLowerCase() === lowerCaseFilterValue;
      } else {
        return product[key].some(
          (tag) => tag.toLowerCase() === lowerCaseFilterValue,
        );
      }
    }
    case BuildABoxFilterCondition.isNotEqual: {
      if (isVariantTitle) {
        return isVariantStyleCollapse
          ? variant && product.hasOnlyDefaultVariant
            ? variant.originalVariantTitle?.toLowerCase() !==
              lowerCaseFilterValue
            : variant[key].toLowerCase() !== lowerCaseFilterValue
          : product.hasOnlyDefaultVariant
            ? product.originalVariantTitle?.toLowerCase() !==
              lowerCaseFilterValue
            : product[key].toLowerCase() !== lowerCaseFilterValue;
      } else {
        return !product[key].some(
          (tag) => tag.toLowerCase() === lowerCaseFilterValue,
        );
      }
    }

    default:
      break;
  }
};

export const filterProducts = ({ selectedFilters, products, variantStyle }) => {
  if (selectedFilters.length === 0) {
    return products;
  }
  const isVariantStyleCollapse = variantStyle === VariantStyle.collapse;
  let newProducts = [];

  if (isVariantStyleCollapse) {
    /*
      1.Iterates through all products
      2.Iterates through all variants of that products and filter
      2.1 Iterates through all selected filter groups
      2.2 Run all selected filters in each variant or product tag
      2.3 Store the filter result in an accumulator with and OR (||) operator
      2.4 In each group we store the result in variantResult and doing an AND (&&) operator with the variantGroup result
      3. Filter new options and values to show in the multi select based on the result of the filters
    */
    newProducts = products
      .map((product) => {
        const filtersGroup = groupBy(selectedFilters, 'groupId');

        const filteredVariants = product.variants.filter((variant) => {
          let variantResult;
          Object.keys(filtersGroup).map((key) => {
            const filters = filtersGroup[key];

            const filterConditional =
              filtersGroup?.[key]?.[0]?.FilterConditional ||
              FilterConditional.OR;

            const variantGroup = filters.reduce(
              (acc, { FilterCondition, FilterType, FilterValue }) =>
                filterByRules({
                  acc,
                  FilterCondition,
                  FilterType,
                  FilterValue,
                  product,
                  variant,
                  isVariantStyleCollapse,
                  filterConditional,
                }),
              undefined,
            );

            variantResult =
              variantResult === undefined
                ? variantGroup
                : variantGroup && variantResult;
          });
          return variantResult;
        });

        const newOptionsAndValues = product.optionsAndValues.filter(
          (optionAndValue) => {
            if (optionAndValue.type === 'title') {
              return true;
            }
            for (let index = 0; index < filteredVariants.length; index++) {
              const variant = filteredVariants[index];
              if (variant.options.includes(optionAndValue.label)) {
                return true;
              }
            }
          },
        );

        const newProduct = {
          ...product,
          variants: filteredVariants,
          optionsAndValues: newOptionsAndValues,
        };

        return newProduct.variants.length > 0 ? newProduct : undefined;
      })
      .filter(Boolean);
  } else {
    /*
      1.Filter all products
      1.1 Iterates through all selected filter groups
      2.2 Run all selected filters in each product
      2.3 Store the filter result in an accumulator with and OR (||) operator
      2.4 In each group we store the result in result and doing an AND (&&) operator with the variantGroup result
    */
    newProducts = products.filter((product) => {
      const filtersGroup = groupBy(selectedFilters, 'groupId');

      let result;
      Object.keys(filtersGroup).map((key) => {
        const filters = filtersGroup[key];
        const filterConditional =
          filtersGroup?.[key]?.[0]?.FilterConditional || FilterConditional.OR;

        const group = filters.reduce(
          (acc, { FilterCondition, FilterType, FilterValue }) =>
            filterByRules({
              acc,
              FilterCondition,
              FilterType,
              FilterValue,
              product,
              isVariantStyleCollapse,
              filterConditional,
            }),
          undefined,
        );

        result = result === undefined ? group : group && result;
      });
      return result;
    });
  }

  return newProducts;
};

export class ExceptionErrorApiObject extends Error {
  cause: { product: string };
  constructor(error: ErrorApiObject) {
    super(error.message);
    this.cause = error.cause;
  }
}
